import { validateQId } from '@exam-management/common/utils';
import {
  type AutocompleteProps,
  type AutocompleteRenderGetTagProps,
  Autocomplete,
  Box,
  Chip,
  Flex,
  TextField,
  Text,
  styled,
} from '@shared/components';
import { capitalize } from '@shared/utils';
import { useCallback, useState } from 'react';
import { type UseControllerProps, type FieldValues, useController } from 'react-hook-form';

import { parseStringToStringArray } from './utils';

const QID_LIMITS = 10;

const StyledTagWrapper = styled(Flex)(({ theme }) => ({
  width: '100%',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

function defaultGetLimitTagsText(more: number) {
  return `+${more}`;
}

function renderChips(values: string[], getTagProps: AutocompleteRenderGetTagProps) {
  return values.map((value, index) => {
    const { key, ...tagProps } = getTagProps({ index });
    const isValid = validateQId(value);
    const color = isValid ? 'default' : 'error';

    return (
      <Chip
        size='medium'
        label={value}
        color={color}
        sx={{
          '&.MuiChip-colorError  .MuiChip-deleteIcon': {
            color: 'error.main',
            '&:hover': {
              color: 'error.dark',
            },
          },
        }}
        key={key}
        {...tagProps}
      />
    );
  });
}

const renderTags: QIDFieldProps['renderTags'] = (qIds, getTagProps, ownerState) => {
  const {
    getLimitTagsText = defaultGetLimitTagsText,
    focused: isFocused,
    limitTags = -1,
    size,
  } = ownerState;

  const more = qIds.length - limitTags;

  // Show more chips than limitTags and focusing on the input
  if (limitTags > -1 && !isFocused && more > 0) {
    const tagsToShow = qIds.slice(0, limitTags);
    return (
      <StyledTagWrapper>
        {renderChips(tagsToShow, getTagProps)}
        <Text
          component='span'
          className={`MuiAutocomplete-tag MuiAutocomplete-tagSize${capitalize(size)}`}
          key={tagsToShow.length}
        >
          {getLimitTagsText(more)}
        </Text>
      </StyledTagWrapper>
    );
  }

  // Using Flex to Ensure that Place holder will be on a new line
  return <StyledTagWrapper>{renderChips(qIds, getTagProps)}</StyledTagWrapper>;
};

type QIDFieldProps = AutocompleteProps<string, true, false, true>;

type FormQIdFieldProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  Omit<QIDFieldProps, 'renderInput' | 'options'> & {
    onClear?: () => void;
  };

export default function FormQIdField<TFieldValues extends FieldValues = FieldValues>(
  props: FormQIdFieldProps<TFieldValues>,
) {
  const [inputValue, setInputValue] = useState('');
  const { onClear, name, ...restProps } = props;

  const {
    field: { disabled: isDisabled, onChange, ref, value },
    fieldState: { isTouched, invalid: isInvalid, error },
    formState: { isSubmitted },
  } = useController({ name });

  const isExceedLimit = value.length > QID_LIMITS;

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text') || '';
      // Trim to remove trailing and starting white space
      const pastedTextArray = parseStringToStringArray(pastedText);
      onChange([...value, ...pastedTextArray]);
      // Clear Input Value on paste
      setInputValue('');
      // Prevent pasted value to trigger onChange
      event.preventDefault();
    },
    [value],
  );

  const onAddValue = useCallback(() => {
    const enteredText = parseStringToStringArray(inputValue);

    if (enteredText.length === 0) return;

    onChange([...value, ...enteredText]);
    setInputValue('');
  }, [inputValue, value]);

  const handleOnBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (inputValue.trim() !== '') {
        onAddValue();
        event.preventDefault();
      }
    },
    [inputValue],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if ([',', 'Enter'].includes(event.key) && inputValue) {
        onAddValue();
        event.preventDefault();
      }
    },
    [inputValue],
  );

  const handleChange: NonNullable<QIDFieldProps['onChange']> = useCallback(
    (event, value, reason) => {
      if (reason === 'createOption') {
        onAddValue();
        return;
      }
      if (reason === 'clear') {
        onChange(value);
        onClear?.();
        return;
      }
      onChange(value);
    },
    [value, onChange],
  );

  return (
    <Box>
      <Text
        color={isExceedLimit ? 'error.main' : 'text.secondary'}
        variant='caption'
        sx={{ textAlign: 'end' }}
      >{`${value.length}/ 10`}</Text>

      <Autocomplete
        multiple
        freeSolo
        disabled={isDisabled}
        ref={ref}
        options={[]}
        value={value ?? []}
        onBlur={handleOnBlur}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        renderTags={renderTags}
        // isOptionEqualToValue defined to prevent Autocomplete from filtering options
        isOptionEqualToValue={() => false}
        renderInput={(params) => (
          <TextField
            {...params}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            error={(isTouched || isSubmitted) && isInvalid}
            helperText={(isTouched || isSubmitted) && error ? error.message : null}
            placeholder='ระบุ QID เพื่อค้นหา'
            slotProps={{
              input: {
                ...params.InputProps,
                sx: {
                  gap: (theme) => theme.spacing(2),
                },
              },
            }}
          />
        )}
        sx={(theme) => ({
          ' .MuiAutocomplete-inputRoot .MuiChip-root': {
            py: theme.spacing(1.5),
            pl: theme.spacing(4),
            pr: theme.spacing(2),
            m: theme.spacing(0),
            height: 'auto',
          },
        })}
        {...restProps}
      />
    </Box>
  );
}
